<template>
  <div class="sp_v2_chat_wrapper">
    <transition name="sp_v2_backdrop_fade">
      <div v-if="isModalMode" class="sp_v2_modal_backdrop" @click="handleBackdropClick"></div>
    </transition>

    <div
      class="sp_v2_chat_interface"
      ref="chatInterfaceRoot"
      :class="{
        'layout-icon': layout === LAYOUTS.ICON,
        'layout-input': layout === LAYOUTS.INPUT,
        'layout-expanded': layout === LAYOUTS.EXPANDED,
        'layout-sidebar': layout === LAYOUTS.SIDEBAR,
        'layout-full': layout === LAYOUTS.FULL,
      }"
    >
      <transition name="welcome-bubble-fade">
        <div v-if="showWelcomeBubble && !isIcon" class="sp_welcome_bubble">
          <div class="sp_welcome_bubble_avatar">
            <img :src="avatarImageUrl" :alt="title || 'Agent Avatar'" class="sp_welcome_avatar_img" />
          </div>
          <div class="sp_welcome_bubble_content">
            <div class="sp_welcome_bubble_text">
              <Typewriter :text="welcomeMessage" :key="welcomeMessage" @onFinish="() => {}" />
            </div>
          </div>
        </div>
      </transition>

      <div class="sp_chat_video_avatar mini" v-if="!isIcon && !isExpanded && !isMobile">
        <video ref="miniVideoAvatar" class="sp_chat_video_avatar_element" :src="`${BASE_SHARE_URL}videos/${videoGreeter}.webm`" playsinline autoplay muted loop></video>
        <div class="sp_chat_video_avatar_status_container">
          <button class="sp_chat_video_avatar_status_button" :class="avatarStatusButtonClass" :style="{ '--audio-level': audioLevel }" @click="() => (isChatRunning ? handleStop() : handleVoiceRecorderToggle())">
            <SoundBarsIcon :isRecording="isRecording || isAudioPlaying || isChatRunning" height="16px" barWidth="2.5px" gap="2.5px"/>
            <span class="sp_chat_video_avatar_status">{{ avatarStatusText }}</span>
          </button>
        </div>
      </div>

      <ChatHeader v-if="isExpanded" :title="title" :subtitle="subtitle" :avatar-image="avatarImage" :is-sidebar-aligned="isSidebarAligned" :is-fullscreen="isFullscreen" :has-playlist-items="hasPlaylistItems" @set-layout="handleSetLayout" @toggle-align="$emit('toggle-align')" @fullscreen="$emit('fullscreen')" />

      <div :class="isModalMode ? 'sp_v2_modal_body' : 'sp_chat_container_area_wrapper'">
        <PlaylistDrawer
          v-if="isModalMode"
          :is-open="hasPlaylistItems"
          :playlist="playlist"
          :is-loading="isPlaylistLoading"
          :selected-content="currentContent"
          @select="handlePlaylistSelect"
        />
        <ContentPanel
          v-if="isModalMode"
          :is-open="isContentPanelOpen || isModalMode"
          :content="currentContent"
          :session-id="sessionId"
          :playlist="playlist"
          :has-prev="hasPrev"
          :has-next="hasNext"
          @close="$emit('close-content')"
          @prev="$emit('playlist-prev')"
          @next="$emit('playlist-next')"
          @progress="(data) => $emit('content-progress', data)"
        />

        <button v-if="isIcon" class="sp_chat_icon_button" @click="handleIconClick">
          <img src="https://embed.showpilot.com/placeholders/images/agent-avatar-icon.png" alt="ShowPilot Agent" class="sp_chat_icon_button_avatar" />
          <span class="sp_chat_icon_button_text">Ask ShowPilot</span>
        </button>

        <div
          v-if="!isIcon"
          class="sp_chat_column"
          :class="{
            'sp_chat_column--modal': isModalMode,
            'sp_v2_modal_chat_column-collapsed': isModalMode && !isDesktop && !isBottomSheetExpanded,
          }"
        >
          <div v-if="isModalMode && !isDesktop" class="sp_v2_modal_chat_column_header" @click="isBottomSheetExpanded = !isBottomSheetExpanded">
            <span class="sp_v2_modal_chat_column_header_handle"></span>
          </div>

          <transition name="avatar-fade">
            <div
              v-if="isExpanded"
              class="sp_chat_video_avatar"
              :class="{ 'modal-avatar': isModalMode, initialized: chatStarted }"
            >
              <video ref="videoAvatar" class="sp_chat_video_avatar_element" :src="`${BASE_SHARE_URL}videos/${videoGreeter}.webm`" playsinline autoplay muted loop></video>
              <div class="sp_chat_video_avatar_status_container">
                <button class="sp_chat_video_avatar_status_button" :class="avatarStatusButtonClass" :style="{ '--audio-level': audioLevel }" @click="() => (isChatRunning ? handleStop() : handleVoiceRecorderToggle())">
                  <SoundBarsIcon :isRecording="isRecording || isAudioPlaying || isChatRunning" height="16px" barWidth="2.5px" gap="2.5px" color="white" recordingColor="white" />
                  <span class="sp_chat_video_avatar_status">{{ avatarStatusText }}</span>
                </button>
              </div>
            </div>
          </transition>

          <div v-if="isExpanded" ref="messagesContainer" class="sp_chat_messages_container" @scroll="handleChatScroll">
            <div class="sp_chat_messages_inner">
              <div v-for="(message, index) in messages" :key="index" :class="['sp_chat_message', message.isAiMessage ? 'ai-message' : 'user-message', 'animated fadeIn faster']" :id="index === messages.length - 1 ? 'sp_chat_message_last' : ''">
                <div class="sp_message_content" v-if="message.message">
                  <div class="sp_message_text">
                    <Markdown :content="message.message" @onLinkClick="handleMarkdownLinkClick" />
                  </div>
                  <div v-if="message.recomendation && message.recomendation.length > 0" class="sp_content_recommendations">
                    <div v-for="(content, idx) in message.recomendation" :key="idx" class="sp_recommendation_card" @click="$emit('open-content', content)">
                      <div v-if="content.image" class="sp_recommendation_image">
                        <img :src="content.image" :alt="content.name" />
                      </div>
                      <div class="sp_recommendation_info">
                        <div class="sp_recommendation_name">{{ content.name }}</div>
                        <div class="sp_recommendation_type">{{ getContentTypeLabel(content.content_extension) }}</div>
                      </div>
                      <div class="sp_recommendation_action">
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <path d="M9 18L15 12L9 6" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" />
                        </svg>
                      </div>
                    </div>
                  </div>
                  <div v-if="message.action === 'book_meeting'" class="sp_meeting_widget_holder"></div>
                </div>
              </div>

              <div v-if="isNavigationPending" class="sp_navigation_badge animated fadeIn faster">
                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                  <polygon points="3 11 22 2 13 21 11 13 3 11"/>
                </svg>
                <span class="sp_navigation_badge_text">Navigating to {{ getNavigationPath(pendingNavigationUrl) }}</span>
                <div class="sp_navigation_badge_spinner"><span></span></div>
              </div>

              <div v-if="!isChatRunning && suggestions && suggestions.length > 0" class="sp_suggestions_holder">
                <div v-for="(suggestion, idx) in suggestions" :key="idx" class="sp_suggestion_card" @click="() => handleSuggestionClick(suggestion)">
                  <div class="sp_suggestion_text">{{ suggestion }}</div>
                </div>
              </div>

              <div v-if="isLoading" class="sp_chat_message ai-message animated fadeIn faster">
                <div class="sp_message_content">
                  <div class="sp_typing_indicator">
                    <span></span>
                    <span></span>
                    <span></span>
                  </div>
                </div>
              </div>
            </div>

            <div v-if="isAtBottom" class="sp_chat_scroll_to_bottom_container">
              <button @click="scrollToBottom" class="sp_chat_scroll_to_bottom_btn">
                <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M12 5v14"/><path d="m19 12-7 7-7-7"/></svg>
              </button>
            </div>
          </div>

          <div class="sp_chat_input_area" :class="{ 'expanded-input': isExpanded }">
            <div class="sp_chat_input_container">
              <textarea name="sp_chat_message_input" ref="messageInput" v-model="inputText" class="sp_chat_input" :placeholder="placeholder" rows="1" @focus="handleFocus" @keydown.enter.exact.prevent="handleSend" @keydown="handleKeyDown" @input="autoResize"></textarea>
              <div class="sp_chat_actions_row">
                <button class="sp_book_demo_btn" @click="() => $emit('showMeetingBooking')">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <rect x="3" y="4" width="18" height="18" rx="2" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" />
                    <path d="M16 2V6" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" />
                    <path d="M8 2V6" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" />
                    <path d="M3 10H21" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" />
                  </svg>
                  <span>Book a demo</span>
                </button>
                <div class="sp_chat_actions_right">
                  <VoiceRecordButton v-if="(!inputText.trim() || isRecording) && !isChatRunning" :isRecording="isRecording" @toggle="handleVoiceRecorderToggle" />
                  <button v-if="(inputText.trim() && !isRecording) || isChatRunning" class="sp_chat_send_btn" :class="{ running: isChatRunning }" @click="isChatRunning ? handleStop() : handleSend()" :disabled="(isLoading || !inputText) && !isChatRunning">
                    <svg v-if="!isChatRunning" xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                      <path d="m5 12 7-7 7 7" />
                      <path d="M12 19V5" />
                    </svg>
                    <svg v-else width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <rect x="6" y="6" width="12" height="12" rx="2" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" />
                    </svg>
                  </button>
                </div>
              </div>
            </div>
            <div v-if="!isModalMode" class="sp_chat_privacy_container">
              <p>By continuing, you agree this conversation may be recorded and used as per our <a href="https://www.showpilot.com/privacy" target="_blank">Privacy Policy</a></p>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import ChatHeader from "./ChatHeader.vue";
import PlaylistDrawer from "./PlaylistDrawer.vue";
import ContentPanel from "./ContentPanel.vue";
import Typewriter from "../../agent/components/Typewriter/index.vue";
import StreamingText from "./components/StreamingText/index.vue";
import Markdown from "./components/Markdown/index.vue";
import VoiceRecordButton from "./components/VoiceRecordButton/index.vue";
import SoundBarsIcon from "./components/SoundBarsIcon/index.vue";
import VoiceRecorderMixin from "../mixins/VoiceRecorder.js";
import MediaQueryMixin from "../mixins/mediaQuery.js";
import Sound from "../../utils/sound/index.js";
import { LAYOUTS } from "../constants/layouts.js";

export default {
  name: "ChatInterfaceV2",
  mixins: [VoiceRecorderMixin, MediaQueryMixin],
  components: {
    ChatHeader,
    PlaylistDrawer,
    ContentPanel,
    Typewriter,
    StreamingText,
    Markdown,
    VoiceRecordButton,
    SoundBarsIcon,
  },
  props: {
    layout: {
      type: String,
      required: true,
    },
    messages: {
      type: Array,
      default: () => [],
    },
    suggestions: {
      type: Array,
      default: () => [],
    },
    isLoading: {
      type: Boolean,
      default: false,
    },
    isChatRunning: {
      type: Boolean,
      default: false,
    },
    isSearching: {
      type: Boolean,
      default: false,
    },
    isFullscreen: {
      type: Boolean,
      default: false,
    },
    title: {
      type: String,
      default: "Chat Assistant",
    },
    subtitle: {
      type: String,
      default: "Online",
    },
    placeholder: {
      type: String,
      default: "Chat with Showpilot agent...",
    },
    avatarImage: {
      type: String,
      default: "",
    },
    videoGreeter: {
      type: String,
      default: 'agent-alex',
    },
    hasPlaylistItems: {
      type: Boolean,
      default: false,
    },
    playlist: {
      type: Array,
      default: () => [],
    },
    isPlaylistLoading: {
      type: Boolean,
      default: false,
    },
    currentContent: {
      type: Object,
      default: null,
    },
    isContentPanelOpen: {
      type: Boolean,
      default: false,
    },
    sessionId: {
      type: String,
      default: null,
    },
    hasPrev: {
      type: Boolean,
      default: false,
    },
    hasNext: {
      type: Boolean,
      default: false,
    },
    isNavigationPending: {
      type: Boolean,
      default: false,
    },
    pendingNavigationUrl: {
      type: String,
      default: null,
    },
  },
  data() {
    return {
      LAYOUTS,
      inputText: "",
      isBottomSheetExpanded: true,
      shouldShowWelcomeBubble: true,
      chatStarted: false,
      isAudioPlaying: false,
      shouldRestartVoiceRecorder: false,
      welcomeGreetingPlayed: false,
      defaultSilenceTimeout: 3000,
      extendedSilenceTimeout: 8000,
      audioLevel: 0,
      scrollAnimationFrame: null,
      lastScrollHeight: 0,
      isAutoScrolling: false,
      userInterruptedScroll: false,
      hasInitialScrolled: false,
      isAtBottom: false,
    };
  },
  computed: {
    isExpanded() {
      return this.layout === LAYOUTS.EXPANDED || this.layout === LAYOUTS.SIDEBAR || this.layout === LAYOUTS.FULL;
    },
    isIcon() {
      return this.layout === LAYOUTS.ICON;
    },
    isSidebarAligned() {
      return this.layout === LAYOUTS.SIDEBAR;
    },
    isModalMode() {
      return this.layout === LAYOUTS.FULL;
    },
    showWelcomeBubble() {
      return !this.isExpanded && this.messages.length > 0 && this.shouldShowWelcomeBubble;
    },
    welcomeMessage() {
      if (this.messages.length > 0 && this.messages[0].message) {
        return this.messages[0].message;
      }
      return "";
    },
    avatarImageUrl() {
      return this.avatarImage || "https://embed.showpilot.com/placeholders/images/agent-avatar.png";
    },
    avatarStatusText() {
      if (this.isRecording) return "Listening...";
      if (this.isAudioPlaying) return "Talking...";
      if (this.isSearching) return "Searching...";
      if (this.isChatRunning) return "Thinking...";
      return "Talk Now";
    },
    avatarStatusButtonClass() {
      return {
        recording: this.isRecording,
        searching: this.isSearching,
        thinking: this.isChatRunning,
        talking: this.isAudioPlaying,
      };
    },
    activeMessagesContainer() {
      return this.$refs.messagesContainer;
    },
  },
  watch: {
    messages: {
      handler(newVal) {
        if (!this.hasInitialScrolled && newVal.length > 0) {
          this.initialScrollRetry();
          return;
        }
      },
      deep: true,
      immediate: true,
    },
    layout(newVal) {
      if (this.isExpanded) {
        this.$nextTick(() => {
          this.focusInput();
          this.scrollToBottom();
        });
        this.shouldShowWelcomeBubble = false;
      }
    },
    isChatRunning(newVal, oldVal) {
      if (oldVal === true && newVal === false) {
        this.checkAndRestartVoiceRecorder();

        this.focusInput();
      }

      this.$nextTick(() => {
        this.checkIfAtBottom();
      });
    },
    isModalMode(newVal) {
      if (newVal) {
        this.$nextTick(() => {
          this.scrollToBottom();
          this.focusInput();
        });
      }
    },
  },
  methods: {
    getRootElement() {
      return this.$refs.chatInterfaceRoot;
    },
    getNavigationPath(url) {
      try {
        return new URL(url).pathname;
      } catch {
        return 'destination';
      }
    },
    handleFocus() {
      if (!this.isExpanded) {
        this.$emit("expand");
      }
    },
    handleIconClick() {
      this.$emit("expand");
    },
    handleSetLayout(layout) {
      this.$emit("set-layout", layout);
    },
    handleBackdropClick() {
      // Optionally close modal on backdrop click
    },
    handlePlaylistSelect(content) {
      this.$emit("select-playlist-content", content);
    },
    handleSend(source = "text") {
      if (this.inputText.trim() && !this.isLoading) {
        if (!this.chatStarted) {
          this.chatStarted = true;
        }
        this.stopVoiceRecognition();
        this.userInterruptedScroll = false;
        this.$emit("send", this.inputText.trim(), source);
        this.handleFocus();
        this.inputText = "";
        this.autoResize();
      }
    },
    handleStop() {
      this.shouldRestartVoiceRecorder = false;
      this.$emit("onChatStop");
    },
    autoResize() {
      const textarea = this.$refs.messageInput;
      if (textarea) {
        textarea.style.height = "auto";
        textarea.style.height = Math.min(textarea.scrollHeight, 120) + "px";
      }
    },
    handleKeyDown() {
      if (!this.chatStarted) {
        this.chatStarted = true;
      }
    },
    initialScrollRetry() {
      const attempt = (tries = 0) => {
        const container = this.$refs.messagesContainer;
        if (container && container.scrollHeight > container.clientHeight) {
          container.scrollTop = container.scrollHeight;
          this.lastScrollHeight = container.scrollHeight;
          this.userInterruptedScroll = false;
          this.hasInitialScrolled = true;
        } else if (!this.hasInitialScrolled && tries < 40) {
          setTimeout(() => attempt(tries + 1), 50);
        }
      };
      attempt();
    },
    scrollToBottom() {
      const container = this.$refs.messagesContainer;

      if (container) {
        container.scrollTop = container.scrollHeight;
        this.lastScrollHeight = container.scrollHeight;
        this.userInterruptedScroll = false;
      }
    },
    smoothScrollToBottom() {
      const container = this.$refs.messagesContainer;
      
      if (!container) return;

      if (this.userInterruptedScroll) return;

      if (this.scrollAnimationFrame) return;

      this.isAutoScrolling = true;
      let lastSetScrollTop = container.scrollTop;

      const onUserScroll = () => {
        if (this.isAutoScrolling && Math.abs(container.scrollTop - lastSetScrollTop) > 15) {
          this.userInterruptedScroll = true;
        }
      };

      container.addEventListener('wheel', onUserScroll, { passive: true });
      container.addEventListener('touchmove', onUserScroll, { passive: true });

      const cleanup = () => {
        container.removeEventListener('wheel', onUserScroll);
        container.removeEventListener('touchmove', onUserScroll);
        this.scrollAnimationFrame = null;
        this.isAutoScrolling = false;
        this.lastScrollHeight = container.scrollHeight;
      };

      const tick = () => {
        if (this.userInterruptedScroll) {
          cleanup();
          return;
        }

        const target = container.scrollHeight - container.clientHeight;
        const current = container.scrollTop;
        const diff = target - current;

        if (diff <= 1) {
          cleanup();
          return;
        }

        const newScrollTop = Math.min(current + Math.max(diff * 0.04, 0.5), target);
        container.scrollTop = newScrollTop;
        lastSetScrollTop = newScrollTop;

        this.scrollAnimationFrame = requestAnimationFrame(tick);
      };

      this.scrollAnimationFrame = requestAnimationFrame(tick);
    },
    smoothScrollToLastMessage() {
      const container = this.$refs.messagesContainer;

      if (container) {
        const containerRect = container.getBoundingClientRect();
        const lastMessage = container.querySelector('#sp_chat_message_last');
        
        if (lastMessage) {
          const lastMessageRect = lastMessage.getBoundingClientRect();
          
          if (!(lastMessageRect.top < containerRect.top)) {
            lastMessage.scrollIntoView({ block: 'start' });
          }
        }
      }
    },
    handleChatScroll() {
     this.checkIfAtBottom();
    },
    checkIfAtBottom() {
      const container = this.$refs.messagesContainer;

      if (container) {
        const percentageScrolled = parseInt((container.scrollTop / (container.scrollHeight - container.clientHeight)) * 100);
        
        this.isAtBottom = (percentageScrolled <= 98);
      }
    },
    setChatStarted(flag) {
      this.chatStarted = flag;
    },
    getContentTypeLabel(extension) {
      const labels = {
        pdf: "PDF Document",
        video: "Video",
        youtube: "YouTube Video",
        html: "Web Content",
      };
      return labels[extension] || "Content";
    },
    focusInput() {
      this.$nextTick(() => {
        const input = this.$refs.messageInput;
        if (input) {
          input.focus();
        }
      });
    },
    handleVoiceRecorderToggle() {
      if (!this.chatStarted) {
        this.chatStarted = true;
      }

      if (this.isAudioPlaying) {
        this.$emit("stop-audio");
        this.setAudioStopped();
        return;
      }

      if (!this.welcomeGreetingPlayed) {
        this.welcomeGreetingPlayed = true;
        this.shouldRestartVoiceRecorder = true;
        this.$emit("play-welcome-greeting");
        return;
      }

      this.shouldRestartVoiceRecorder = false;

      if (!this.isRecording) {
        this.setSilenceTimeoutDuration(this.defaultSilenceTimeout);
        this.pauseAndResetVideo();
      }

      this.toggleVoiceRecognition();
    },
    pauseAndResetVideo() {
      const videos = [this.$refs.miniVideoAvatar, this.$refs.videoAvatar];
      videos.forEach(video => {
        if (video) {
          video.pause();
          video.currentTime = 0;
        }
      });
    },
    resumeVideo() {
      const videos = [this.$refs.miniVideoAvatar, this.$refs.videoAvatar];
      videos.forEach(video => {
        if (video) video.play();
      });
    },
    onVoiceTextRecognized(event) {
      if (this.silenceTimeoutDuration !== this.defaultSilenceTimeout) {
        this.setSilenceTimeoutDuration(this.defaultSilenceTimeout);
      }

      if (event.type === "final") {
        this.inputText = this.inputText.replace(this.interimTranscript, "") + event.text;
      } else if (event.type === "interim") {
        const baseText = this.inputText.replace(this.interimTranscript, "");
        this.inputText = baseText + event.text;
      }
      this.autoResize();
    },
    onVoiceRecordingStopped() {
      this.inputText = this.inputText.replace(this.interimTranscript, "");
      this.autoResize();
      this.resumeVideo();
    },
    onVoiceRecordingStarted() {
      Sound.record();
    },
    onVoiceRecognitionError(error) {
      console.error("Voice recognition error:", error);
    },
    onSilenceTimeoutReached() {
      if (this.inputText.trim()) {
        this.shouldRestartVoiceRecorder = true;
        this.handleSend("voice");
      }
    },
    setAudioPlaying() {
      this.isAudioPlaying = true;
      this.pauseAndResetVideo();
    },
    setAudioStopped() {
      this.isAudioPlaying = false;
      this.audioLevel = 0;
      if (!this.isRecording && !this.isChatRunning) {
        this.resumeVideo();
      }
    },
    updateAudioLevel(level) {
      this.audioLevel = level;
    },
    checkAndRestartVoiceRecorder() {
      if (this.shouldRestartVoiceRecorder && !this.isChatRunning && !this.isAudioPlaying) {
        this.shouldRestartVoiceRecorder = false;
        setTimeout(() => {
          if (!this.isRecording) {
            this.setSilenceTimeoutDuration(this.extendedSilenceTimeout);
            this.startVoiceRecognition();
          }
        }, 300);
      }
    },
    handleMarkdownLinkClick(data) {
      this.$emit('navigateToUrl', data.link);
    },
    handleSuggestionClick(suggestion) {
      if (!this.chatStarted) {
        this.chatStarted = true;
      }
      this.$emit('onSuggestionClick', suggestion);
    },
  },
  mounted() {
    if (this.isExpanded) {
      this.focusInput();
    }
  },
  beforeDestroy() {
    if (this.scrollAnimationFrame) {
      cancelAnimationFrame(this.scrollAnimationFrame);
      this.scrollAnimationFrame = null;
    }
  },
};
</script>

<style lang="scss">
/* ===== Modal Backdrop ===== */
.sp_v2_modal_backdrop {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.132);
  backdrop-filter: blur(4px);
  z-index: 9998;
}

.sp_v2_backdrop_fade-enter-active,
.sp_v2_backdrop_fade-leave-active {
  transition: opacity 0.3s ease;
}

.sp_v2_backdrop_fade-enter,
.sp_v2_backdrop_fade-leave-to {
  opacity: 0;
}

/* ===== Main Chat Interface ===== */
.sp_v2_chat_interface {
  position: fixed;
  bottom: 20px;
  right: 50%;
  transform: translate(50%, 0%);
  z-index: 9999;
  background: #ffffff;
  border: 1px solid var(--sp-chat-primary);
  border-radius: 10px;
  display: flex;
  flex-direction: column;
  pointer-events: none;

  transition-property: border, border-radius, box-shadow, width, height, right, bottom, top, left, opacity, transform, padding, margin;
  transition-duration: 0.4s;
  transition-timing-function: cubic-bezier(0.4, 0, 0.2, 1);

  & > * {
    pointer-events: auto;
  }

  &.layout-input {
    width: 560px;
    height: 140px;
    max-width: 90dvw;
    border-radius: 24px;
    padding: 0;
  }

  &.layout-expanded {
    width: 560px;
    height: clamp(300px, 90vh, 700px);
    height: clamp(300px, 90dvh, 700px);
    max-width: 90dvw;
    max-height: 90vh;
    max-height: 90dvh;
    border-radius: 16px;
    border-color: #e5e7ea;
    box-shadow: rgba(0, 0, 0, 0.19) 0px 10px 20px, rgba(0, 0, 0, 0.23) 0px 6px 6px;

    .sp_chat_column {
      overflow: hidden;
    }
  }

  &.layout-sidebar {
    right: 0;
    bottom: 50%;
    transform: translate(0%, 50%);
    width: 400px;
    height: 100vh;
    height: 100dvh;
    border: 1px solid #e5e7ea;
    border-radius: 0;
    border-right: none;
    border-top: none;
    border-bottom: none;
    box-shadow: rgba(0, 0, 0, 0.19) 0px 10px 20px, rgba(0, 0, 0, 0.23) 0px 6px 6px;
  }

  &.layout-full {
    bottom: 50%;
    right: 50%;
    transform: translate(50%, 50%);
    width: calc(100vw - 32px);
    height: calc(100vh - 32px);
    height: calc(100dvh - 32px);
    max-width: 1680px;
    max-height: 900px;
    border-radius: 16px;
    border: 1px solid #e5e7ea;
    box-shadow: 0 25px 60px rgba(0, 0, 0, 0.3);
    overflow: hidden;
    pointer-events: auto;

    transition-property: all;
    transition-duration: 0.4s;
    transition-timing-function: ease-in-out;

    .sp_chat_video_avatar_element {
      border-radius: 0;
    }

    .sp_chat_column {
      overflow: hidden;
    }
  }

  &.layout-icon {
    width: 400px;
    height: 100vh;
    height: 100dvh;
    right: 0;
    bottom: 0px;
    transform: none;
    border: none;
    background: transparent;
    box-shadow: none;
    cursor: default;
    overflow: visible;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: none;
    animation: none;

    .sp_chat_container_area_wrapper {
      align-items: flex-end;
      justify-content: flex-end;
    }
  }

  .sp_chat_container_area_wrapper {
    width: 100%;
    height: 90%;
    display: flex;
    flex-direction: column;
    border-radius: 24px;
    pointer-events: none;

    & > * {
      pointer-events: auto;
    }
  }

  &.layout-input {
    .sp_chat_input_container {
      border-color: transparent;
      padding: 10px 18px 5px 18px;
    }

    .sp_chat_privacy_container {
      transition-delay: 0.4s;
      transition-property: background-color;
      padding-bottom: 10px;
    }
  }

  &.layout-sidebar {
    .sp_chat_container_area_wrapper{
      max-width: 400px;
    }

    .sp_chat_column {
      overflow: hidden;
    }
  }
}

/* ===== Modal Body: 3-column layout ===== */
.sp_v2_modal_body {
  display: flex;
  flex: 1;
  min-height: 0;
  overflow: hidden;
}

/* ===== Unified Chat Column ===== */
.sp_chat_column {
  display: flex;
  flex-direction: column;
  flex: 1;

  &.sp_chat_column--modal {
    width: 380px;
    min-width: 320px;
    max-width: 380px;
    flex-shrink: 0;
    flex: unset;
  }
}

.sp_v2_modal_chat_column_header {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 14px 12px 12px;
  flex-shrink: 0;
  background: #ffffff;
  cursor: pointer;
}

.sp_v2_modal_chat_column_header_handle {
  position: absolute;
  left: 50%;
  top: 8px;
  transform: translateX(-50%);
  width: 40px;
  height: 4px;
  background: #d1d5db;
  border-radius: 2px;
}

/* ===== Video Avatar (reused styles) ===== */
.sp_chat_video_avatar {
  position: relative;
  display: block;
  width: calc(100% - 32px);
  height: 200px;
  border-radius: 24px;
  border: 1px solid #fff;
  margin: 16px;
  overflow: hidden;
  transition: height 0.3s cubic-bezier(0.4, 0, 0.2, 1);

  .sp_chat_video_avatar_element {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    object-fit: cover;
    border-radius: 12px;
    transition: top 0.3s ease-out;
  }

  .sp_chat_video_avatar_status_container {
    position: absolute;
    width: 100%;
    bottom: 0;
    display: flex;
    justify-content: center;
    margin-bottom: 10px;
    z-index: 1;

    .sp_chat_video_avatar_status_button {
      display: inline-flex;
      align-items: center;
      gap: 8px;
      padding: 8px 16px;
      background: var(--sp-chat-primary);
      color: white;
      font-size: 13px;
      font-weight: 600;
      border-radius: 20px;
      cursor: pointer;
      transition: all 0.2s;
      border: none;
      box-shadow: 0 2px 4px rgba(11, 51, 152, 0.2);
      will-change: transform;
      backface-visibility: hidden;
      -webkit-font-smoothing: subpixel-antialiased;

      &:hover {
        background: #0a2c7a;
        transform: translateY(-1px);
        box-shadow: 0 4px 8px rgba(11, 51, 152, 0.3);
      }

      &.recording {
        background: #dc2626;
        box-shadow: 0 2px 4px rgba(220, 38, 38, 0.2);
        animation: pulse-recording 1.5s infinite;
        &:hover { background: #b91c1c; }
      }

      &.searching {
        background: #f59e0b;
        box-shadow: 0 2px 4px rgba(245, 158, 11, 0.2);
        animation: pulse-searching 2s infinite;
        &:hover { background: #d97706; }
      }

      &.thinking {
        background: #7c3aed;
        box-shadow: 0 2px 4px rgba(124, 58, 237, 0.2);
        animation: pulse-thinking 2s infinite;
        &:hover { background: #6d28d9; }
      }

      &.talking {
        --audio-level: 0;
        background: #059669;
        box-shadow: 0 2px 4px rgba(5, 150, 105, 0.2);
        transition: transform 0.1s ease-out, box-shadow 0.1s ease-out;
        transform: scale(calc(1 + var(--audio-level) * 0.5)) translateZ(0);
        box-shadow: 0 2px 4px rgba(5, 150, 105, 0.2), 0 0 0 calc(var(--audio-level) * 8px) rgba(5, 150, 105, calc(0.4 - var(--audio-level) * 0.4));
        &:hover { background: #047857; }
      }
    }
  }

  &.mini {
    position: absolute;
    left: -150px;
    overflow: visible;
    opacity: 0;
    animation: display-in 0.3s;
    animation-delay: 0.6s;
    animation-fill-mode: forwards;
    width: 140px;
    height: 100%;
    border-radius: 50%;
    border: none;
    margin: 0;
    padding: 0;

    .sp_chat_video_avatar_status_button {
      background: #fff;
      color: var(--sp-chat-primary);
      &:hover {
        background: #fff;
        color: var(--sp-chat-primary);
      }
      &.recording { color: white; }
    }
  }

  &.initialized {
    width: 100%;
    height: 52px;
    margin: 0px;

    .sp_chat_video_avatar_element {
      top: -110%;
    }
    .sp_chat_video_avatar_status_container {
      margin: 10px 0px;
      margin-left: 16px;
      justify-content: flex-start;
    }
  }

  &.modal-avatar {
    width: 100%;
    margin: 0;
    border-radius: 0;
    border: none;
    flex-shrink: 0;
  }

  &.modal-avatar.initialized {
    height: 52px;
  }
}


/* ===== Input Area ===== */
.sp_chat_input_area {
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);

  &.expanded-input {
    padding: 16px 20px;
    border-top: 1px solid #e5e7ea;
    border-radius: 0 0 16px 16px;
  }

  .sp_chat_input_container {
    padding: 5px 15px 15px 15px;
    border: 1px solid #e5e7ea;
    border-radius: 10px;
    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);

    &:focus-within {
      border-color: var(--sp-chat-primary);
    }

    .sp_chat_input {
      width: 100%;
      border: none;
      outline: none;
      resize: none;
      font-size: 14px;
      line-height: 1.5;
      color: #060e2b;
      font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif;
      background: transparent;
      margin: 15px 0;
      max-height: 120px;
      overflow-y: auto;
      scrollbar-width: thin;
      scrollbar-color: #e5e7ea transparent;

      &::placeholder { color: #7e7f80; }
      &::-webkit-scrollbar { width: 4px; }
      &::-webkit-scrollbar-track { background: transparent; }
      &::-webkit-scrollbar-thumb {
        background: #e5e7ea;
        border-radius: 2px;
      }
    }

    .sp_chat_actions_row {
      display: flex;
      justify-content: space-between;

      .sp_chat_actions_right {
        display: flex;
        gap: 10px;
      }

      .sp_book_demo_btn {
        display: flex;
        align-items: center;
        gap: 8px;
        padding: 8px 16px;
        background: var(--sp-chat-primary);
        color: #ffffff;
        border: none;
        border-radius: 7px;
        font-size: 14px;
        font-weight: 500;
        cursor: pointer;
        transition: all 0.2s ease;
        white-space: nowrap;
        flex-shrink: 0;

        svg {
          width: 16px;
          height: 16px;
          stroke: currentColor;
        }

        &:hover {
          background: var(--sp-chat-primary);
          transform: translateY(-1px);
        }

        &:active { transform: translateY(0); }
      }

      .sp_chat_send_btn {
        flex-shrink: 0;
        width: 36px;
        height: 36px;
        border-radius: 10px;
        border: none;
        background: var(--sp-chat-primary);
        color: #ffffff;
        cursor: pointer;
        display: flex;
        align-items: center;
        justify-content: center;
        transition: all 0.2s ease;
        padding: 0;

        &:hover:not(:disabled) {
          background: var(--sp-chat-primary);
          transform: scale(1.05);
        }
        &:active:not(:disabled) { transform: scale(0.95); }

        &:disabled {
          background: #e5e7ea;
          svg path { stroke: var(--sp-chat-primary); }
        }

        svg {
          width: 18px;
          height: 18px;
        }

        &.running {
          background: #e5e7ea;
          color: #060e2b;
          &:hover { background: #e5e7ea; }
          &:active { transform: none; }
          svg rect {
            stroke: red;
            fill: red;
          }
        }
      }
    }
  }

  .sp_chat_privacy_container {
    width: 100%;
    display: flex;
    justify-content: center;

    p {
      text-align: center;
      color: #989898;
      font-size: 10px;

      a {
        color: var(--sp-chat-primary);
        text-decoration: none;
      }
    }
  }
}

.layout-input {
  .sp_chat_input_container {
    &:focus-within {
      border-color: transparent;
    }
  }
}

/* ===== Messages Container ===== */
.sp_chat_messages_container {
  position: relative;
  flex: 1;
  overflow-y: auto;
  overflow-x: hidden;
  padding: 16px 20px;
  background: #f9fafb;
  min-height: 0;
  scrollbar-width: thin;
  scrollbar-color: #e5e7ea transparent;

  &::-webkit-scrollbar { width: 6px; }
  &::-webkit-scrollbar-track { background: transparent; }
  &::-webkit-scrollbar-thumb {
    background: #e5e7ea;
    border-radius: 3px;
  }

  .sp_chat_messages_inner {
    display: flex;
    flex-direction: column;
    gap: 16px;

    .sp_chat_message {
      display: flex;
      animation-duration: 0.3s;

      &.ai-message {
        justify-content: flex-start;
        .sp_message_content {
          background: #f2f3f5;
          color: #060e2b;
          border-radius: 16px 16px 16px 4px;
          max-width: 100%;
        }
      }

      &.user-message {
        justify-content: flex-end;
        .sp_message_content {
          background: var(--sp-chat-primary);
          color: #ffffff;
          border-radius: 16px 16px 4px 16px;
          max-width: 80%;
        }
      }

      .sp_message_content {
        padding: 12px 16px;
        font-size: 14px;
        line-height: 1.5;
        word-wrap: break-word;

        .sp_message_text {
          white-space: pre-wrap;
        }

        .sp_content_recommendations {
          margin-top: 12px;
          display: flex;
          flex-direction: column;
          gap: 8px;

          .sp_recommendation_card {
            display: flex;
            align-items: center;
            gap: 12px;
            padding: 12px;
            background: #ffffff;
            border-radius: 12px;
            cursor: pointer;
            transition: all 0.2s ease;
            border: 1px solid #e5e7ea;

            &:hover {
              background: #f9fafb;
              border-color: var(--sp-chat-primary);
              transform: translateX(4px);
            }

            .sp_recommendation_image {
              width: 48px;
              height: 48px;
              border-radius: 8px;
              overflow: hidden;
              flex-shrink: 0;
              background: #f2f3f5;
              img {
                width: 100%;
                height: 100%;
                object-fit: cover;
              }
            }

            .sp_recommendation_info {
              flex: 1;
              min-width: 0;
              .sp_recommendation_name {
                font-size: 13px;
                font-weight: 600;
                color: #060e2b;
                margin-bottom: 2px;
                overflow: hidden;
                text-overflow: ellipsis;
                white-space: nowrap;
              }
              .sp_recommendation_type {
                font-size: 11px;
                color: #6b7280;
              }
            }

            .sp_recommendation_action {
              flex-shrink: 0;
              color: var(--sp-chat-primary);
            }
          }
        }

        .sp_typing_indicator {
          display: flex;
          gap: 4px;
          padding: 4px 0;

          span {
            position: relative;
            width: 8px;
            height: 8px;
            border-radius: 50%;
            background: #6b7280;
            animation: typingDot 1.4s infinite;
            &:nth-child(2) { animation-delay: 0.2s; }
            &:nth-child(3) { animation-delay: 0.4s; }
          }
        }
      }
    }

    .sp_navigation_badge {
      display: flex;
      align-items: center;
      justify-content: center;
      gap: 10px;
      color: var(--sp-chat-primary);
      font-size: 14px;
      font-weight: 500;
      animation: navigationBadgeSlideIn 0.3s cubic-bezier(0.4, 0, 0.2, 1);
      margin-bottom: 12px;

      svg {
        flex-shrink: 0;
        width: 18px;
        height: 18px;
      }

      .sp_navigation_badge_text {
        line-height: 1.4;
      }

      .sp_navigation_badge_spinner {
        flex-shrink: 0;

        span {
          display: block;
          width: 14px;
          height: 14px;
          border: 2px solid #e5e7ea;
          border-top-color: var(--sp-chat-primary);
          border-radius: 50%;
          animation: navigationSpinner 0.8s linear infinite;
        }
      }
    }

    .sp_suggestions_holder {
      display: flex;
      flex-wrap: wrap;
      gap: 8px;
      margin-top: 8px;
      justify-content: flex-end;

      .sp_suggestion_card {
        display: inline-flex;
        align-items: center;
        padding: 10px 16px;
        background: #ffffff;
        border: 1px solid #e5e7ea;
        border-radius: 20px;
        cursor: pointer;
        transition: all 0.2s ease;
        max-width: fit-content;

        &:hover {
          background: #f9fafb;
          border-color: var(--sp-chat-primary);
          transform: translateY(-1px);
          box-shadow: 0 2px 8px rgba(75, 96, 235, 0.15);
        }
        &:active { transform: translateY(0); }

        .sp_suggestion_text {
          font-size: 12px;
          line-height: 1.4;
          color: #060e2b;
          font-weight: 500;
          white-space: nowrap;
        }
      }
    }
  }
}

/* ===== Welcome Bubble ===== */
.sp_welcome_bubble {
  position: absolute;
  bottom: calc(100% + 12px);
  left: 50%;
  transform: translateX(-50%);
  display: flex;
  align-items: center;
  gap: 12px;
  background: #f2f3f5;
  border-radius: 16px 16px 16px 4px;
  padding: 12px 16px;
  max-width: 320px;
  min-width: 90%;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  z-index: 10000;
  animation: welcomeBubbleSlideIn 0.4s cubic-bezier(0.4, 0, 0.2, 1);

  &::after {
    content: "";
    position: absolute;
    bottom: -7px;
    left: 20px;
    width: 0;
    height: 0;
    border-left: 8px solid transparent;
    border-right: 8px solid transparent;
    border-top: 8px solid #f2f3f5;
  }

  .sp_welcome_bubble_avatar {
    flex-shrink: 0;
    .sp_welcome_avatar_img {
      width: 25px;
      height: 25px;
      object-fit: cover;
      border-radius: 50%;
    }
  }

  .sp_welcome_bubble_content {
    flex: 1;
    min-width: 0;
    .sp_welcome_bubble_text {
      font-size: 13px;
      line-height: 1.5;
      color: #060e2b;
      word-wrap: break-word;
    }
  }
}

/* ===== Icon Button ===== */
.sp_chat_icon_button {
  width: max-content;
  height: max-content;
  border: none;
  margin-right: 20px;
  border-radius: 999px;
  background: #fff;
  border: 2px solid var(--sp-chat-primary);
  padding: 15px 20px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  position: relative;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
  transition: all 0.2s ease;

  .sp_chat_icon_button_avatar {
    width: 30px;
    height: 30px;
    object-fit: cover;
    border-radius: 50%;
  }

  .sp_chat_icon_button_text {
    color: var(--sp-chat-primary);
    font-size: 15px;
    font-weight: 600;
    white-space: nowrap;
    font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif;
  }

  &:hover { scale: 1.05; }
}

.sp_v2_chat_wrapper {
  position: relative;
  display: inline-block;
}

/* ===== Animations ===== */
.welcome-bubble-fade-enter-active { transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1); }
.welcome-bubble-fade-leave-active { transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1); }
.welcome-bubble-fade-enter { opacity: 0; bottom: calc(100% + 2px); }
.welcome-bubble-fade-enter-to { opacity: 1; bottom: calc(100% + 12px); }
.welcome-bubble-fade-leave { opacity: 1; bottom: calc(100% + 12px); }
.welcome-bubble-fade-leave-to { opacity: 0; bottom: calc(100% + 22px); }

.avatar-fade-enter-active { transition: opacity 0.2s ease 0.25s, transform 0.2s ease 0.25s; }
.avatar-fade-leave-active { transition: none; }
.avatar-fade-enter { opacity: 0; transform: scale(0.95); }
.avatar-fade-enter-to { opacity: 1; transform: scale(1); }
.avatar-fade-leave-to { opacity: 0; }

@keyframes typingDot {
  0%, 60%, 100% { top: 0; opacity: 0.7; }
  30% { top: -8px; opacity: 1; }
}

@keyframes welcomeBubbleSlideIn {
  from { opacity: 0; bottom: calc(100% + 2px); }
  to { opacity: 1; bottom: calc(100% + 12px); }
}

@keyframes pulse-recording {
  0%, 100% { box-shadow: 0 2px 4px rgba(220, 38, 38, 0.2), 0 0 0 0 rgba(220, 38, 38, 0.4); }
  50% { box-shadow: 0 2px 4px rgba(220, 38, 38, 0.2), 0 0 0 6px rgba(220, 38, 38, 0); }
}

@keyframes pulse-searching {
  0%, 100% { box-shadow: 0 2px 4px rgba(245, 158, 11, 0.2), 0 0 0 0 rgba(245, 158, 11, 0.4); }
  50% { box-shadow: 0 2px 4px rgba(245, 158, 11, 0.2), 0 0 0 6px rgba(245, 158, 11, 0); }
}

@keyframes pulse-thinking {
  0%, 100% { box-shadow: 0 2px 4px rgba(124, 58, 237, 0.2), 0 0 0 0 rgba(124, 58, 237, 0.4); }
  50% { box-shadow: 0 2px 4px rgba(124, 58, 237, 0.2), 0 0 0 6px rgba(124, 58, 237, 0); }
}

@keyframes display-in {
  from { opacity: 0; }
  to { opacity: 1; }
}

@keyframes navigationBadgeSlideIn {
  from {
    opacity: 0;
    transform: translateY(-10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@keyframes navigationArrowMove {
  0%, 100% {
    transform: translateX(0);
  }
  50% {
    transform: translateX(4px);
  }
}

@keyframes navigationSpinner {
  to {
    transform: rotate(360deg);
  }
}

/* ===== Responsive: Tablet ===== */
@media (max-width: 1023px) {
  .sp_v2_chat_interface {
    &.layout-input {
      bottom: 20px;
      top: auto;
    }

    &.layout-expanded {
      width: 100dvw;
      height: 100vh;
      height: 100dvh;
      max-width: 100dvw;
      max-height: 100vh;
      max-height: 100dvh;
      bottom: 0;
      right: 0;
      top: auto;
      left: 0;
      transform: none;
      border-radius: 0;
      border: none;
    }

    &.layout-sidebar {
      right: 0;
      bottom: 0;
      top: auto;
      width: 100dvw;
      height: 100vh;
      height: 100dvh;
      max-width: 100dvw;
      max-height: 100vh;
      max-height: 100dvh;
      left: 0;
      transform: none;
      border-radius: 0;
      border: none;
    }

    &.layout-full {
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      width: 100dvw;
      height: 100vh;
      height: 100dvh;
      max-width: 100dvw;
      max-height: 100vh;
      max-height: 100dvh;
      transform: none;
      border-radius: 0;
    }
  }

  .sp_v2_modal_body {
    flex-direction: column;
    position: relative;
  }

  .sp_chat_column--modal {
    position: absolute;
    top: 0;
    right: 0;
    bottom: 0;
    z-index: 10;
    width: 100%;
    max-width: 100%;
    min-width: 0;
    flex: 1;
    transition: top 0.3s ease;

    border-top-left-radius: 16px;
    border-top-right-radius: 16px;
  }

  .sp_v2_modal_chat_column-collapsed {
    top: 90%;
  }
}

/* ===== Responsive: Mobile ===== */
@media (max-width: 767px) {
  .sp_v2_chat_interface {
    &.layout-input {
      width: calc(100dvw - 32px);
      left: 50%;
      bottom: 20px;
      top: auto;
      transform: translateX(-50%);
    }

    &.layout-expanded {
      width: 100dvw;
      height: 100vh;
      height: 100dvh;
      max-width: 100dvw;
      max-height: 100vh;
      max-height: 100dvh;
      bottom: 0;
      right: 0;
      top: auto;
      left: 50%;
      transform: translateX(-50%);
      border-radius: 0;
      border: none;
    }

    &.layout-sidebar {
      right: 16px;
      bottom: 20px;
      top: auto;
      width: 100dvw;
      height: 100vh;
      height: 100dvh;
      max-width: 100dvw;
      max-height: 100vh;
      max-height: 100dvh;
      right: 0;
      left: 50%;
      bottom: 0;
      transform: translateX(-50%);
      border-radius: 0;
      border: none;
    }

    &.layout-full {
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      width: 100dvw;
      height: 100vh;
      height: 100dvh;
      max-width: 100dvw;
      max-height: 100vh;
      max-height: 100dvh;
      transform: none;
      border-radius: 0;
    }

    &.layout-icon {
      right: 16px;
      bottom: 16px;
      top: auto;
    }
  }

  .sp_v2_modal_body {
    flex-direction: column;
  }

  .sp_chat_column--modal {
    width: 100%;
    max-width: 100%;
    min-width: 0;
    flex: 1;
  }

  .sp_chat_messages_container {
    padding: 12px 16px;
  }

  .sp_chat_input_area {
    &.expanded-input {
      padding: 12px 16px;
    }
  }

  .sp_welcome_bubble {
    max-width: calc(100dvw - 64px);
    left: 50%;
    transform: translateX(-50%);
    &::after { left: 20px; }
  }
}

.sp_chat_scroll_to_bottom_container {
  position: sticky;
  bottom: 0;
  left: 0;
  display: flex;
  justify-content: flex-end;
  pointer-events: none;
}

.sp_chat_scroll_to_bottom_btn {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border-radius: 50%;
  border: 1px solid var(--sp-chat-primary);
  background: #fff;
  color: var(--sp-chat-primary);
  cursor: pointer;
  pointer-events: all;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.15);
  transition: background 0.15s, box-shadow 0.15s, scale 0.15s;
  padding: 0;

  &:hover {
    box-shadow: 0 3px 8px rgba(0, 0, 0, 0.2);
    scale: 1.05;
  }
  
  &:active {
    scale: 0.95;
  }

  svg {
    display: block;
    flex-shrink: 0;
    
    path {
      stroke: currentColor;
    }
  }
}
</style>
